import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { useAuth } from '../AuthContext';
import { 
  checkAdminStatus,
  getAllTasks, 
  getAdminStats, 
  updateTask,
  updateDocumentStatus,
  downloadDocument
} from '../api';
import { 
  Users, 
  ClipboardList, 
  Clock, 
  CheckCircle2,
  IndianRupee,
  FileText,
  ChevronRight,
  ChevronDown,
  Download,
  Check,
  X,
  AlertCircle,
  Calendar,
  MessageSquare,
  User,
  Mail,
  Phone,
  RefreshCw
} from 'lucide-react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { 
  TASK_STATUS, 
  VALID_STATUSES, 
  STATUS_DISPLAY,
  normalizeStatus 
} from '../constants/statusConstants';

// Use canonical status display config
const STATUS_CONFIG = {
  [TASK_STATUS.INTAKE]: STATUS_DISPLAY[TASK_STATUS.INTAKE],
  [TASK_STATUS.REVIEW]: STATUS_DISPLAY[TASK_STATUS.REVIEW],
  [TASK_STATUS.FILING]: STATUS_DISPLAY[TASK_STATUS.FILING],
  [TASK_STATUS.WAITING]: STATUS_DISPLAY[TASK_STATUS.WAITING],
  [TASK_STATUS.DONE]: STATUS_DISPLAY[TASK_STATUS.DONE],
  [TASK_STATUS.CANCELLED]: STATUS_DISPLAY[TASK_STATUS.CANCELLED]
};

// Canonical task statuses for dropdown
const TASK_STATUSES = VALID_STATUSES;
const DOCUMENT_STATUSES = ['pending', 'verified', 'rejected'];

// Document status badge
const DocumentStatusBadge = ({ status }) => {
  const config = {
    pending: { label: 'Pending', color: 'bg-slate-100 text-slate-600' },
    verified: { label: 'Verified', color: 'bg-emerald-100 text-emerald-700' },
    rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700' }
  };
  const { label, color } = config[status] || config.pending;
  return <Badge className={`${color} text-xs`}>{label}</Badge>;
};

// Stats Card
const StatCard = ({ icon: Icon, label, value, color }) => (
  <Card>
    <CardContent className="p-4">
      <div className="flex items-center gap-3">
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-900">{value}</p>
          <p className="text-xs text-slate-500">{label}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Task Row Component
const TaskRow = ({ task, onUpdate, onRefresh }) => {
  const [expanded, setExpanded] = useState(false);
  // Normalize the task status on init
  const normalizedTaskStatus = normalizeStatus(task.status);
  const [status, setStatus] = useState(normalizedTaskStatus);
  const [estimatedCompletion, setEstimatedCompletion] = useState(task.estimated_completion || '');
  const [adminNotes, setAdminNotes] = useState(task.admin_notes || '');
  const [saving, setSaving] = useState(false);

  // Use normalized status for display
  const statusConfig = STATUS_CONFIG[normalizedTaskStatus] || STATUS_CONFIG[TASK_STATUS.INTAKE];

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateTask(task.id, {
        status: status,
        estimated_completion: estimatedCompletion || null,
        admin_notes: adminNotes || null
      });
      toast.success('Task updated');
      onRefresh();
    } catch (error) {
      toast.error('Failed to update task');
    } finally {
      setSaving(false);
    }
  };

  const handleDocumentStatus = async (docId, newStatus) => {
    try {
      await updateDocumentStatus(docId, newStatus);
      toast.success(`Document ${newStatus}`);
      onRefresh();
    } catch (error) {
      toast.error('Failed to update document');
    }
  };

  const handleDownload = async (docId, fileName) => {
    try {
      const response = await downloadDocument(docId);
      const { file_data } = response.data;
      
      // Create download link
      const link = document.createElement('a');
      link.href = `data:application/octet-stream;base64,${file_data}`;
      link.download = fileName;
      link.click();
      
      toast.success('Download started');
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  return (
    <Card className="mb-3">
      <CardContent className="p-0">
        {/* Header Row - Always visible */}
        <div 
          className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center gap-4 flex-1">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-medium text-slate-900">{task.service_name}</h4>
                <Badge className={`${statusConfig.color} border text-xs`}>
                  {statusConfig.label}
                </Badge>
              </div>
              <div className="flex items-center gap-4 text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <User className="h-3 w-3" />
                  {task.user?.name || 'Unknown'}
                </span>
                <span className="flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  {task.user?.email || 'N/A'}
                </span>
                <span>₹{task.amount?.toLocaleString()}</span>
                <span>{format(new Date(task.created_at), 'MMM dd, yyyy')}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">
              {task.documents?.length || 0} docs
            </span>
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </div>
        </div>

        {/* Expanded Content */}
        {expanded && (
          <div className="border-t border-slate-100 p-4 bg-slate-50/50">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Task Management */}
              <div className="space-y-4">
                <h5 className="font-medium text-slate-700 text-sm">Task Management</h5>
                
                {/* Status */}
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Status</label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TASK_STATUSES.map(s => (
                        <SelectItem key={s} value={s}>
                          {STATUS_CONFIG[s]?.label || s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Estimated Completion */}
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Estimated Completion Date</label>
                  <Input 
                    type="date"
                    value={estimatedCompletion ? estimatedCompletion.split('T')[0] : ''}
                    onChange={(e) => setEstimatedCompletion(e.target.value)}
                  />
                </div>

                {/* Admin Notes */}
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Internal Notes (Admin only)</label>
                  <Textarea 
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Add internal notes..."
                    rows={3}
                  />
                </div>

                {/* Save Button */}
                <Button 
                  onClick={handleSave} 
                  disabled={saving}
                  className="w-full"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>

                {/* User Notes */}
                {task.notes && (
                  <div className="p-3 bg-white rounded-lg border">
                    <p className="text-xs text-slate-500 mb-1">User Notes:</p>
                    <p className="text-sm text-slate-700">{task.notes}</p>
                  </div>
                )}
              </div>

              {/* Right Column - Documents */}
              <div className="space-y-4">
                <h5 className="font-medium text-slate-700 text-sm">Documents ({task.documents?.length || 0})</h5>
                
                {task.documents && task.documents.length > 0 ? (
                  <div className="space-y-2">
                    {task.documents.map((doc) => (
                      <div 
                        key={doc.id}
                        className="flex items-center justify-between p-3 bg-white rounded-lg border"
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <FileText className="h-4 w-4 text-slate-400 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-700 truncate">
                              {doc.file_name}
                            </p>
                            <p className="text-xs text-slate-400">
                              {doc.uploaded_at ? format(new Date(doc.uploaded_at), 'MMM dd, yyyy HH:mm') : 'N/A'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <DocumentStatusBadge status={doc.status || 'pending'} />
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => handleDownload(doc.id, doc.file_name)}
                              title="Download"
                            >
                              <Download className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              onClick={() => handleDocumentStatus(doc.id, 'verified')}
                              title="Verify"
                            >
                              <Check className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDocumentStatus(doc.id, 'rejected')}
                              title="Reject"
                            >
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-400">
                    <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No documents uploaded yet</p>
                  </div>
                )}

                {/* User Contact Info */}
                <div className="p-3 bg-white rounded-lg border mt-4">
                  <h6 className="text-xs text-slate-500 mb-2">User Contact</h6>
                  <div className="space-y-1 text-sm">
                    <p className="flex items-center gap-2">
                      <User className="h-3.5 w-3.5 text-slate-400" />
                      {task.user?.name || 'N/A'}
                    </p>
                    <p className="flex items-center gap-2">
                      <Mail className="h-3.5 w-3.5 text-slate-400" />
                      {task.user?.email || 'N/A'}
                    </p>
                    {task.user?.phone && (
                      <p className="flex items-center gap-2">
                        <Phone className="h-3.5 w-3.5 text-slate-400" />
                        {task.user.phone}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Main Admin Dashboard Component
const AdminDashboard = () => {
  const { user, loading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    const checkAdmin = async () => {
      if (!loading && !user) {
        navigate('/login');
        return;
      }

      if (user) {
        try {
          const response = await checkAdminStatus();
          if (!response.data.is_admin) {
            toast.error('Admin access required');
            navigate('/dashboard');
            return;
          }
          setIsAdmin(true);
        } catch {
          toast.error('Admin access required');
          navigate('/dashboard');
          return;
        } finally {
          setCheckingAdmin(false);
        }
      }
    };

    checkAdmin();
  }, [user, loading, navigate]);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [tasksRes, statsRes] = await Promise.all([
        getAllTasks(),
        getAdminStats()
      ]);
      // Normalize status for all tasks
      const normalizedTasks = tasksRes.data.map(task => ({
        ...task,
        status: normalizeStatus(task.status)
      }));
      setTasks(normalizedTasks);
      setStats(statsRes.data);
    } catch {
      toast.error('Failed to load admin data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [isAdmin, fetchData]);

  // Filter tasks using canonical status values
  const filteredTasks = statusFilter === 'all' 
    ? tasks 
    : tasks.filter(t => normalizeStatus(t.status) === statusFilter);

  if (loading || checkingAdmin) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="h-8 w-8 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500">Checking admin access...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="container mx-auto px-4 md:px-8 py-4 max-w-7xl">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-slate-900">Admin Panel</h1>
              <p className="text-sm text-slate-500">Manage compliance tasks and documents</p>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                size="sm"
                onClick={fetchData}
                disabled={isLoading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/dashboard')}
              >
                User Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 py-6 max-w-7xl">
        {/* Stats - Using canonical status groups */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <StatCard 
              icon={ClipboardList}
              label="Total Tasks"
              value={stats.total_orders}
              color="bg-blue-100 text-blue-600"
            />
            <StatCard 
              icon={Clock}
              label="Intake"
              value={stats.pending_intake || 0}
              color="bg-blue-100 text-blue-600"
            />
            <StatCard 
              icon={AlertCircle}
              label="In Progress"
              value={stats.in_progress || 0}
              color="bg-violet-100 text-violet-600"
            />
            <StatCard 
              icon={CheckCircle2}
              label="Done"
              value={stats.done || 0}
              color="bg-emerald-100 text-emerald-600"
            />
            <StatCard 
              icon={IndianRupee}
              label="Revenue"
              value={`₹${stats.total_revenue?.toLocaleString() || 0}`}
              color="bg-green-100 text-green-600"
            />
          </div>
        )}

        {/* Status breakdown - Only canonical statuses */}
        {stats?.by_status && (
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-2">
                <span className="text-sm text-slate-500 mr-2">By Status:</span>
                {VALID_STATUSES.map((status) => {
                  const count = stats.by_status[status] || 0;
                  return count > 0 ? (
                    <Badge 
                      key={status} 
                      variant="outline"
                      className={`cursor-pointer hover:bg-slate-100 ${statusFilter === status ? 'bg-slate-200' : ''}`}
                      onClick={() => setStatusFilter(status)}
                    >
                      {STATUS_CONFIG[status]?.label || status}: {count}
                    </Badge>
                  ) : null;
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filter */}
        <div className="flex items-center gap-2 mb-4">
          <span className="text-sm text-slate-500">Filter:</span>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tasks</SelectItem>
              {TASK_STATUSES.map(s => (
                <SelectItem key={s} value={s}>
                  {STATUS_CONFIG[s]?.label || s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-sm text-slate-400 ml-2">
            Showing {filteredTasks.length} of {tasks.length} tasks
          </span>
        </div>

        {/* Tasks List */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="h-8 w-8 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin mx-auto mb-4" />
            <p className="text-slate-500">Loading tasks...</p>
          </div>
        ) : filteredTasks.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <ClipboardList className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No tasks found</h3>
              <p className="text-slate-500">
                {statusFilter !== 'all' ? 'Try changing the filter' : 'No tasks have been created yet'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div>
            {filteredTasks.map(task => (
              <TaskRow 
                key={task.id} 
                task={task} 
                onRefresh={fetchData}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
